'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import cookies from 'js-cookie';
import { registerContact } from '@/actions/hubspot/registerContact';
import { emailRegex, isCorporateEmail } from '@/utils';

interface FooterNewsletterRedesignProps {
  inviteText?: string;
  emailPlaceholder?: string;
  buttonLabel?: string;
  disclaimer?: string;
  formId?: string;
  hubspotPortalIdForm?: string;
}

interface IForm {
  email: string;
}

enum FormStatus {
  DIRTY = 'DIRTY',
  SUBMITTED = 'SUBMITTED',
  FAILED = 'FAILED',
}

export const FooterNewsletterRedesign = ({
  inviteText,
  emailPlaceholder = 'Enter your email',
  buttonLabel = 'Subscribe',
  disclaimer,
  formId = '',
  hubspotPortalIdForm = '7119515',
}: FooterNewsletterRedesignProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors: { email: emailError },
      isSubmitting,
    },
  } = useForm<IForm>({ mode: 'onBlur' });

  const [status, setStatus] = useState<FormStatus>(FormStatus.DIRTY);

  const formSubmit = async (values: IForm) => {
    reset();
    try {
      const { postSubmitAction } = await registerContact({
        properties: { email: values.email },
        hubspotPortalIdForm,
        hubspotGuidForm: formId,
        hubspotutkCookie: cookies.get('hubspotutk'),
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName:
          typeof document !== 'undefined'
            ? document.title
            : 'Footer Newsletter',
      });
      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.assign(postSubmitAction.value);
        return;
      }
      setStatus(FormStatus.SUBMITTED);
    } catch {
      setStatus(FormStatus.FAILED);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {inviteText && (
        <p className="text-[16px] lg:text-[14px] xl:text-[16px] leading-[1.5] text-[#eaeaea]">
          {inviteText}
        </p>
      )}

      {status === FormStatus.SUBMITTED ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-3 rounded-xl border border-[#00f557]/25 bg-[#00f557]/[0.06] px-4 py-3"
        >
          <svg
            className="mt-0.5 h-5 w-5 shrink-0 text-[#00f557]"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.5 7.6a1 1 0 0 1-1.42.006l-3.5-3.5a1 1 0 1 1 1.414-1.414l2.79 2.79 6.793-6.887a1 1 0 0 1 1.417-.009Z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex flex-col gap-0.5">
            <p className="text-[16px] font-semibold leading-[1.4] text-[#00f557]">
              You&apos;re subscribed!
            </p>
            <p className="text-[14px] leading-[1.5] text-[#eaeaea]/70">
              Thanks for joining — we&apos;ll send our latest insights straight
              to your inbox.
            </p>
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(formSubmit)}
          className="flex items-start gap-6"
        >
          <div className="flex flex-1 flex-col">
            <div
              className="relative flex h-[48px] items-center border-b border-[#aaa] px-3 py-2"
              suppressHydrationWarning
            >
              <input
                type="email"
                placeholder=" "
                aria-label={emailPlaceholder}
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: emailRegex,
                    message: 'Please enter a valid email',
                  },
                  validate: {
                    isCorporateEmail: (value) =>
                      isCorporateEmail(value) || 'Please use a corporate email',
                  },
                })}
                className="peer w-full bg-transparent text-[14px] leading-[1.5] text-[#eaeaea] outline-none"
                suppressHydrationWarning
              />
              <span className="pointer-events-none absolute left-3 text-[14px] leading-[1.5] peer-focus:hidden peer-[:not(:placeholder-shown)]:hidden">
                <span className="text-[#eaeaea]/50">{emailPlaceholder} </span>
                <span className="text-[#00f557]">*</span>
              </span>
            </div>
            {emailError && (
              <p className="mt-1 text-[12px] text-red-400">
                {emailError.message}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="group/cta-btn inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#00f557] px-6 py-3 text-[16px] font-bold leading-[1.5] text-[#002726] transition-colors hover:bg-[#59B264] focus-visible:bg-[#59B264] active:bg-[#59B264] disabled:opacity-50"
          >
            <span className="underline-offset-2 group-hover/cta-btn:underline group-focus-visible/cta-btn:underline group-active/cta-btn:underline">
              {isSubmitting ? 'Subscribing...' : buttonLabel}
            </span>
          </button>
        </form>
      )}

      {status === FormStatus.FAILED && (
        <p className="text-[12px] text-red-400">
          Something went wrong. Please try again.
        </p>
      )}

      {disclaimer && (
        <p className="text-[12px] leading-[1.5] text-[#eaeaea]">{disclaimer}</p>
      )}
    </div>
  );
};
